import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  SUPPORTED_CURRENCIES, 
  DEFAULT_CURRENCY, 
  getCurrencyByLocale,
  formatPrice as formatPriceUtil,
  getCurrencyOptions 
} from '../config/currencies';
import { getTokenAuth } from '../utils/authUtils';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentCurrency, setCurrentCurrency] = useState(DEFAULT_CURRENCY);
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState(null);

  // Écouter les changements d'utilisateur connecté
  useEffect(() => {
    const handleStorageChange = () => {
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          console.log('[CurrencyContext] User data updated:', parsedUser);
          setUser(prevUser => {
            // Éviter le cycle infini - ne mettre à jour que si différent
            if (JSON.stringify(prevUser) !== JSON.stringify(parsedUser)) {
              return parsedUser;
            }
            return prevUser;
          });
        } catch (error) {
          console.warn('Error parsing user data:', error);
          setUser(null);
        }
      } else {
        console.log('[CurrencyContext] No user data found, setting user to null');
        setUser(prevUser => prevUser !== null ? null : prevUser);
      }
    };

    // Éviter l'appel initial immédiat - laisser AuthContext se stabiliser
    let isInitialCall = true;
    const stableHandleStorageChange = () => {
      if (isInitialCall) {
        isInitialCall = false;
        // Délai pour laisser AuthContext se stabiliser d'abord
        setTimeout(() => {
          handleStorageChange();
        }, 100);
      } else {
        handleStorageChange();
      }
    };

    // Écouter les changements de localStorage (depuis d'autres onglets)
    window.addEventListener('storage', handleStorageChange);
    
    // Charger l'utilisateur initial avec délai
    stableHandleStorageChange();

    // Écouter les changements du même onglet via un événement personnalisé
    const handleUserUpdate = () => {
      console.log('[CurrencyContext] Custom user update event received');
      handleStorageChange();
    };
    
    window.addEventListener('userDataUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserUpdate);
    };
  }, []); // Dépendance vide pour éviter le cycle

  // Initialiser la devise au chargement
  useEffect(() => {
    // Éviter les re-initialisations inutiles
    if (isInitialized) {
      return;
    }

    const initializeCurrency = () => {
      try {
        const userExplicitChoice = localStorage.getItem('userExplicitCurrencyChoice');
        const savedCurrency = localStorage.getItem('preferredCurrency');
        
        // 1. Si l'utilisateur est connecté et a une devise préférée, TOUJOURS l'utiliser
        // (nettoyer le choix explicite pour permettre la synchronisation avec le profil)
        if (user && user.preferred_currency && SUPPORTED_CURRENCIES[user.preferred_currency]) {
          console.log('[CurrencyContext] User connected with preferred currency:', user.preferred_currency);
          setCurrentCurrency(prevCurrency => {
            if (prevCurrency !== user.preferred_currency) {
              localStorage.setItem('preferredCurrency', user.preferred_currency);
              localStorage.removeItem('userExplicitCurrencyChoice');
              return user.preferred_currency;
            }
            return prevCurrency;
          });
          setIsInitialized(true);
          return;
        }

        // 2. Si l'utilisateur a fait un choix explicite ET qu'il n'est pas connecté ou sans devise préférée
        if (userExplicitChoice === 'true' && savedCurrency && SUPPORTED_CURRENCIES[savedCurrency]) {
          console.log('[CurrencyContext] User explicit choice detected:', savedCurrency);
          setCurrentCurrency(prevCurrency => prevCurrency !== savedCurrency ? savedCurrency : prevCurrency);
          setIsInitialized(true);
          return;
        }

        // 3. Si utilisateur connecté SANS devise préférée, utiliser localStorage ou détecter
        if (user && !user.preferred_currency) {
          console.log('[CurrencyContext] User connected without preferred currency');
          if (savedCurrency && SUPPORTED_CURRENCIES[savedCurrency]) {
            setCurrentCurrency(prevCurrency => prevCurrency !== savedCurrency ? savedCurrency : prevCurrency);
          } else {
            const detectedCurrency = getCurrencyByLocale();
            setCurrentCurrency(prevCurrency => {
              if (prevCurrency !== detectedCurrency) {
                localStorage.setItem('preferredCurrency', detectedCurrency);
                return detectedCurrency;
              }
              return prevCurrency;
            });
          }
          setIsInitialized(true);
          return;
        }

        // 4. Utilisateur non connecté : utiliser localStorage ou détecter (UNE SEULE FOIS)
        if (!user) {
          console.log('[CurrencyContext] No user connected, using localStorage or detection');
          if (savedCurrency && SUPPORTED_CURRENCIES[savedCurrency]) {
            setCurrentCurrency(prevCurrency => prevCurrency !== savedCurrency ? savedCurrency : prevCurrency);
          } else {
            const detectedCurrency = getCurrencyByLocale();
            setCurrentCurrency(prevCurrency => {
              if (prevCurrency !== detectedCurrency) {
                localStorage.setItem('preferredCurrency', detectedCurrency);
                return detectedCurrency;
              }
              return prevCurrency;
            });
          }
          setIsInitialized(true);
          return;
        }
        
      } catch (error) {
        console.warn('Error initializing currency:', error);
        setCurrentCurrency(DEFAULT_CURRENCY);
        setIsInitialized(true);
      }
    };

    console.log('[CurrencyContext] Initializing currency, user:', user);
    initializeCurrency();
  }, [user]); // Seulement dépendant de user

  // Mettre à jour la devise quand la langue change
  useEffect(() => {
    if (!isInitialized) return;
    
    // Si l'utilisateur n'a pas explicitement choisi une devise,
    // on peut l'ajuster selon la nouvelle langue
    const savedCurrency = localStorage.getItem('preferredCurrency');
    const userExplicitChoice = localStorage.getItem('userExplicitCurrencyChoice');
    
    if (!userExplicitChoice && !savedCurrency) {
      const newDetectedCurrency = getCurrencyByLocale();
      if (newDetectedCurrency !== currentCurrency) {
        setCurrentCurrency(newDetectedCurrency);
        localStorage.setItem('preferredCurrency', newDetectedCurrency);
      }
    }
  }, [i18n.language, isInitialized, currentCurrency]);

  // Changer la devise
  const changeCurrency = async (newCurrencyCode) => {
    if (SUPPORTED_CURRENCIES[newCurrencyCode]) {
      console.log('[CurrencyContext] Changing currency to:', newCurrencyCode);
      console.log('[CurrencyContext] User connected:', !!user);
      console.log('[CurrencyContext] Token available:', !!getTokenAuth());
      
      setCurrentCurrency(newCurrencyCode);
      localStorage.setItem('preferredCurrency', newCurrencyCode);
      localStorage.setItem('userExplicitCurrencyChoice', 'true');

      // Si l'utilisateur est connecté, sauvegarder dans son profil
      if (user && getTokenAuth()) {
        console.log('[CurrencyContext] Attempting to save currency preference to database...');
        try {
          const response = await fetch('/api/v1/auth/update-profile', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getTokenAuth()}`
            },
            body: JSON.stringify({
              name: user.name,
              email: user.email,
              phone: user.phone,
              preferred_currency: newCurrencyCode
            })
          });

          console.log('[CurrencyContext] API Response status:', response.status);
          console.log('[CurrencyContext] API Response ok:', response.ok);

          if (response.ok) {
            // Mettre à jour seulement le localStorage sans changer l'état user
            // pour éviter de redéclencher l'effet de réinitialisation
            const updatedUser = { ...user, preferred_currency: newCurrencyCode };
            localStorage.setItem('userData', JSON.stringify(updatedUser));
            
            // Déclencher un événement pour notifier les autres contextes
            window.dispatchEvent(new CustomEvent('userDataUpdated'));
            
            console.log('[CurrencyContext] ✅ Currency preference saved to user profile successfully');
          } else {
            const errorData = await response.json();
            console.error('[CurrencyContext] ❌ Failed to save currency preference:', response.status, errorData);
          }
        } catch (error) {
          console.error('[CurrencyContext] ❌ Network error saving currency preference:', error);
        }
      } else {
        console.log('[CurrencyContext] ⚠️ User not connected or no token, skipping database save');
      }
    } else {
      console.warn('[CurrencyContext] ❌ Unsupported currency:', newCurrencyCode);
    }
  };

  // Formater un prix selon la devise actuelle
  const formatPrice = (amount) => {
    if (!isInitialized) return 'N/A';
    
    // Déterminer la locale selon la langue actuelle
    let locale = 'fr-FR';
    switch (i18n.language) {
      case 'en':
        locale = 'en-US';
        break;
      case 'ar':
        locale = 'ar-DZ';
        break;
      default:
        locale = 'fr-FR';
    }
    
    return formatPriceUtil(amount, currentCurrency, locale);
  };

  // Obtenir les informations de la devise actuelle
  const getCurrentCurrencyInfo = () => {
    return SUPPORTED_CURRENCIES[currentCurrency] || SUPPORTED_CURRENCIES[DEFAULT_CURRENCY];
  };

  // Obtenir les options de devises pour un sélecteur
  const getCurrencySelectOptions = () => {
    return getCurrencyOptions(i18n.language);
  };

  // Obtenir le symbole de la devise actuelle
  const getCurrencySymbol = () => {
    return getCurrentCurrencyInfo().symbol;
  };

  const contextValue = {
    currentCurrency,
    changeCurrency,
    formatPrice,
    getCurrentCurrencyInfo,
    getCurrencySelectOptions,
    getCurrencySymbol,
    isInitialized,
    supportedCurrencies: SUPPORTED_CURRENCIES
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
